import type { ImportSkipReason, ImportTemplatesResult } from 'modules/manager/ui/hooks/use-import-templates';
import React from 'react';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

type RejectedError = Extract<ImportTemplatesResult, { status: 'rejected' }>['error'];

const REJECTED_TEXT: Record<RejectedError, string> = {
  'not-json': 'Это не файл шаблонов ImGame',
  'wrong-format': 'Это не файл шаблонов ImGame',
  'newer-version': 'Файл сделан в более новой версии админки — обновите страницу',
  empty: 'В файле нет ни одного шаблона',
  'read-failed': 'Не удалось прочитать файл',
};

const SKIP_TEXT: Record<ImportSkipReason, string> = {
  'not-object': 'запись в файле повреждена',
  'no-name': 'у шаблона нет названия',
  'no-objects': 'нет содержимого холста',
  'bad-size': 'неверный размер холста',
  'unknown-template-type': 'такой тип шаблона эта версия админки не знает',
  'name-taken': 'уже есть общий шаблон с таким именем',
  'no-free-name': 'не удалось подобрать свободное имя',
  'request-failed': 'не удалось сохранить — ошибка сети или сервера',
};

interface ImportTemplatesResultModalProps {
  result: ImportTemplatesResult;
  onClose: () => void;
}

/** Outcome of a template file import: what was created, under which name, and what was skipped. */
function ImportTemplatesResultModal({ result, onClose }: ImportTemplatesResultModalProps) {
  if (result.status === 'rejected') {
    return (
      <Modal show onClose={onClose} size="md">
        <Modal.Header title="Файл не загружен" subtitle={result.fileName} />
        <Modal.Body>
          <UDText variant="body-2" className="text-CaseErrorText">
            {REJECTED_TEXT[result.error]}
          </UDText>
          <UDText variant="body-3" className="text-inkSecondary mt-2">
            Ни один шаблон не создан.
          </UDText>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant="primary" onClick={onClose}>
            Понятно
          </UDButton>
        </Modal.Footer>
      </Modal>
    );
  }

  const { created, overwritten, skipped, total } = result;

  return (
    <Modal show onClose={onClose} size="md">
      <Modal.Header title={`Импортировано ${created.length + overwritten.length} из ${total}`} subtitle={result.fileName} />
      {/* No scroll of its own: `UDModal` already scrolls past 90vh, and a nested
          `overflow-y: auto` showed a scrollbar for a single line — text overflows its
          line box by a pixel or two. */}
      <Modal.Body className="flex flex-col gap-4">
        {created.length > 0 && (
          <section>
            <UDText variant="body-2" className="text-CaseSuccessText mb-1 font-semibold">
              Создано
            </UDText>
            <ul className="flex flex-col gap-1" aria-label="Созданные шаблоны">
              {created.map((template) => (
                <li key={template.id}>
                  <UDText variant="body-3" className="text-inkPrimary">
                    {template.name}
                    {template.name !== template.sourceName && (
                      <span className="text-inkSecondary"> — в файле «{template.sourceName}», имя было занято</span>
                    )}
                  </UDText>
                </li>
              ))}
            </ul>
          </section>
        )}
        {overwritten.length > 0 && (
          <section>
            <UDText variant="body-2" className="text-CasePendingText mb-1 font-semibold">
              Перезаписано
            </UDText>
            <ul className="flex flex-col gap-1" aria-label="Перезаписанные шаблоны">
              {overwritten.map((template) => (
                <li key={template.id}>
                  <UDText variant="body-3" className="text-inkPrimary">
                    {template.name}
                  </UDText>
                </li>
              ))}
            </ul>
          </section>
        )}
        {skipped.length > 0 && (
          <section>
            <UDText variant="body-2" className="text-CaseErrorText mb-1 font-semibold">
              Пропущено
            </UDText>
            <ul className="flex flex-col gap-1" aria-label="Пропущенные шаблоны">
              {skipped.map((entry) => (
                <li key={entry.index}>
                  <UDText variant="body-3" className="text-inkPrimary">
                    {entry.name ?? `Шаблон №${entry.index + 1}`}
                    <span className="text-inkSecondary"> — {SKIP_TEXT[entry.reason]}</span>
                  </UDText>
                </li>
              ))}
            </ul>
          </section>
        )}
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="primary" onClick={onClose}>
          Готово
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
}

export default ImportTemplatesResultModal;
